/**
 * SearchTablePage Template
 *
 * Complete page wrapper for searchable property tables
 * Combines search, filters, bulk actions, and virtualized table
 * Responsive design with mobile-friendly layout
 */

"use client";

import React, { useState, useMemo, useCallback } from "react";

import {
  Input,
  Button,
  Card,
  CardBody,
  Divider,
  Breadcrumbs,
  BreadcrumbItem,
} from "@nextui-org/react";
import { Search, Filter, RefreshCw, Download, Plus } from "lucide-react";

import { useBulkActions, BulkAction } from "@/hooks/useBulkActions";
import Loader from "@/ui/Loader";
import { cn } from "@/utils/cn";

import { BulkActionsPanel } from "./BulkActionsPanel";
import {
  VirtualizedPropertyTable,
  PropertyTableColumn,
} from "./VirtualizedPropertyTable";

export interface SearchFilter {
  key: string;
  label: string;
  type: "text" | "select" | "date" | "range" | "boolean";
  options?: Array<{ label: string; value: any }>;
  placeholder?: string;
  value?: any;
}

export interface SearchTablePageProps<T = any> {
  /** Page title */
  title: string;
  /** Page description */
  description?: string;
  /** Breadcrumb items */
  breadcrumbs?: Array<{ label: string; href?: string }>;

  /** Data */
  data: T[];
  columns: PropertyTableColumn<T>[];
  getRowKey: (item: T) => string;

  /** Search and filters */
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: SearchFilter[];
  onFilterChange?: (key: string, value: any) => void;

  /** Bulk actions */
  bulkActions: BulkAction[];
  onBulkAction: (
    actionId: string,
    selectedItems: T[],
  ) => Promise<{ success: boolean; message?: string }>;

  /** Loading states */
  isLoading?: boolean;
  isRefreshing?: boolean;

  /** Action buttons */
  primaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  secondaryActions?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }>;

  /** Table configuration */
  itemHeight?: number;
  tableHeight?: number;
  enableVirtualization?: boolean;

  /** Export functionality */
  onExport?: () => void;

  /** Refresh functionality */
  onRefresh?: () => void;

  /** Custom empty state */
  emptyContent?: React.ReactNode;

  /** Layout options */
  showFilters?: boolean;
  showBreadcrumbs?: boolean;
  containerClassName?: string;

  /** Row interactions */
  onRowClick?: (item: T, index: number) => void;
  getRowClassName?: (item: T, index: number) => string;
}

export function SearchTablePage<T = any>({
  title,
  description,
  breadcrumbs = [],
  data,
  columns,
  getRowKey,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  filters = [],
  onFilterChange,
  bulkActions,
  onBulkAction,
  isLoading = false,
  isRefreshing = false,
  primaryAction,
  secondaryActions = [],
  itemHeight = 60,
  tableHeight = 600,
  enableVirtualization = true,
  onExport,
  onRefresh,
  emptyContent,
  showFilters = true,
  showBreadcrumbs = true,
  containerClassName,
  onRowClick,
  getRowClassName,
}: SearchTablePageProps<T>) {
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // Bulk actions hook
  const bulkActionsHook = useBulkActions({
    items: data,
    getItemKey: getRowKey,
    actions: bulkActions.reduce(
      (acc, action) => {
        acc[action.id] = async (selectedItems: T[]) => {
          return await onBulkAction(action.id, selectedItems);
        };
        return acc;
      },
      {} as Record<string, any>,
    ),
    enableUndo: true,
  });

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;

    const searchLower = searchValue.toLowerCase();
    return data.filter((item) => {
      // Search in all string fields of the item
      return Object.values(item as Record<string, any>).some(
        (value) =>
          value &&
          typeof value === "string" &&
          value.toLowerCase().includes(searchLower),
      );
    });
  }, [data, searchValue]);

  // Handle search input
  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value);
      // Clear selection when search changes
      bulkActionsHook.clearSelection();
    },
    [onSearchChange, bulkActionsHook],
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    onRefresh?.();
    bulkActionsHook.clearSelection();
  }, [onRefresh, bulkActionsHook]);

  return (
    <div className={cn("flex flex-col h-full bg-gray-50", containerClassName)}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        {/* Breadcrumbs */}
        {showBreadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs className="mb-3">
            {breadcrumbs.map((crumb, index) => (
              <BreadcrumbItem
                key={index}
                href={crumb.href}
                className={!crumb.href ? "text-gray-500" : undefined}
              >
                {crumb.label}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
        )}

        {/* Title and Description */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {description && <p className="mt-1 text-gray-600">{description}</p>}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {secondaryActions.map((action, index) => (
              <Button
                key={index}
                variant="bordered"
                startContent={action.icon}
                onPress={action.onClick}
                size="sm"
              >
                {action.label}
              </Button>
            ))}

            {onExport && (
              <Button
                variant="bordered"
                startContent={<Download size={16} />}
                onPress={onExport}
                size="sm"
              >
                Export
              </Button>
            )}

            {onRefresh && (
              <Button
                variant="bordered"
                startContent={<RefreshCw size={16} />}
                onPress={handleRefresh}
                isLoading={isRefreshing}
                size="sm"
              >
                Refresh
              </Button>
            )}

            {primaryAction && (
              <Button
                color="primary"
                startContent={primaryAction.icon || <Plus size={16} />}
                onPress={primaryAction.onClick}
                size="sm"
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 max-w-md">
            <Input
              placeholder={searchPlaceholder}
              startContent={
                <Search
                  size={16}
                  className="text-gray-400"
                />
              }
              value={searchValue}
              onValueChange={handleSearchChange}
              className="w-full"
            />
          </div>

          {/* Filters Toggle */}
          {showFilters && filters.length > 0 && (
            <Button
              variant="bordered"
              startContent={<Filter size={16} />}
              onPress={() => setShowFiltersPanel(!showFiltersPanel)}
              size="sm"
            >
              Filters
            </Button>
          )}

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              `${filteredData.length} result${filteredData.length !== 1 ? "s" : ""}`
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFiltersPanel && filters.length > 0 && (
          <>
            <Divider className="my-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filters.map((filter) => (
                <div key={filter.key}>
                  <Input
                    label={filter.label}
                    placeholder={filter.placeholder}
                    value={filter.value || ""}
                    onChange={(e) =>
                      onFilterChange?.(filter.key, e.target.value)
                    }
                    size="sm"
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Table Container */}
      <div className="flex-1 p-6">
        <Card className="h-full">
          <CardBody className="p-0">
            {enableVirtualization ? (
              <VirtualizedPropertyTable
                data={filteredData}
                columns={columns}
                selectedKeys={bulkActionsHook.selectedKeys}
                onSelectionChange={bulkActionsHook.setSelectedKeys}
                getRowKey={getRowKey}
                itemHeight={itemHeight}
                height={tableHeight}
                isLoading={isLoading}
                emptyContent={emptyContent}
                onRowClick={onRowClick}
                getRowClassName={getRowClassName}
                className="h-full"
              />
            ) : (
              // Fallback to regular table for smaller datasets
              <div className="p-4">
                <p className="text-gray-600">
                  Regular table implementation would go here
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Bulk Actions Panel */}
      <BulkActionsPanel
        selectedCount={bulkActionsHook.selectedCount}
        actions={bulkActions}
        onActionExecute={bulkActionsHook.executeAction}
        onClearSelection={bulkActionsHook.clearSelection}
        onUndo={bulkActionsHook.undoLastAction}
        canUndo={bulkActionsHook.canUndo}
        undoActionLabel={bulkActionsHook.undoActionLabel}
        position="bottom"
      />
    </div>
  );
}

export default SearchTablePage;
